// useDemoMode - Convenience hook for onboarding pages to handle demo mode
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdmin } from '../context/AdminContext'
import { useOnboarding } from '../context/OnboardingContext'
import { STAGE_INFO } from '../config/stages'

export const useDemoMode = () => {
  const { isDemoMode, demoMode, stopDemoMode } = useAdmin()
  const { transitionToStage } = useOnboarding()
  const navigate = useNavigate()

  // Get the next stage path for "Continue" buttons in demo mode
  const getNextStagePath = useCallback(() => {
    if (!demoMode?.stage) return '/admin'

    const stageOrder = [
      '02_INTEREST_CONFIRMED',
      '03_GENERIC_DEMO',
      '03B_CONSENT_INITIATE',
      '04_CONSENT_PENDING',
      '05_CONSENT_SIGNED',
      '06_API_SETUP',
      '07_API_CONNECTED',
      '10_DEMO_SCHEDULED',
      '11_PROVISIONED'
    ]

    const currentIndex = stageOrder.indexOf(demoMode.stage)
    if (currentIndex === -1 || currentIndex >= stageOrder.length - 1) {
      return '/admin'
    }

    const nextStage = stageOrder[currentIndex + 1]
    return STAGE_INFO[nextStage]?.path || '/admin'
  }, [demoMode?.stage])

  // Simulates successful completion without API call
  // Returns true if in demo mode (and simulation was started), false otherwise
  const simulateSuccess = useCallback((callback, delay = 1500) => {
    if (!isDemoMode) return false

    setTimeout(() => {
      callback?.()
    }, delay)
    return true
  }, [isDemoMode])

  // Simulate transitioning to the next stage (for multi-step demos)
  const simulateStageTransition = useCallback((nextStage, callback) => {
    if (!isDemoMode) return false

    // Update local stage in context
    transitionToStage(nextStage)

    // Navigate to the next stage's path
    const nextPath = STAGE_INFO[nextStage]?.path
    if (nextPath) {
      setTimeout(() => {
        callback?.()
        navigate(nextPath)
      }, 500)
    }
    return true
  }, [isDemoMode, navigate, transitionToStage])

  // Exit demo and return to admin
  const exitDemo = useCallback(() => {
    stopDemoMode()
    navigate('/admin')
  }, [stopDemoMode, navigate])

  return {
    isDemoMode,
    demoStage: demoMode?.stage,
    simulateSuccess,
    simulateStageTransition,
    getNextStagePath,
    exitDemo,
  }
}
